<template>
  <form @submit.prevent="submit">
    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            General Information
          </h3>

          <p>
            In this panel, you can modify the title and company of the article.
          </p>

          <button v-if="edit" type="button" class="btn btn-black btn-sm" @click="$emit('edit-author')">
            <i class="fa fa-pencil-alt"></i> Edit Author
          </button>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <TextFormGroup
                v-model="data.title"
                name="title"
                label="Title"
                help-text="The title of the article."
                :error="(errors.title || [])[0]"
            />

            <TextareaFormGroup
                v-model="data.subheading"
                name="subheading"
                label="Subheading"
                help-text="The subheading of the article."
                :error="(errors.subheading || [])[0]"
            />

            <TextFormGroup
                v-model="data.author_name_override"
                name="author_name_override"
                label="Author Name Override"
                help-text="Override the name of the author of the article (leave empty to use the name of the author)."
                :error="(errors.author_name_override || [])[0]"
                :placeholder="data.author_name"
            />

            <RadioFormGroup
                v-model="data.show_author"
                name="show_author"
                label="Show Author"
                :error="(errors.show_author || [])[0]"
                :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
            />

            <GalleryFormGroup
                v-model="data.image_id"
                name="image_id"
                label="Top Image"
                help-text="The image of the article."
                :error="(errors.image_id || [])[0]"
                :nullable="true"
                :company-id="companyId"
            />

            <TextareaFormGroup
                v-model="data.image_text"
                name="image_text"
                label="Top Image Text"
                help-text="The image text of the article."
                :error="(errors.image_text || [])[0]"
            />

            <SelectFormGroup
                v-model="data.format"
                name="format"
                label="Top Image Format"
                :error="(errors.format || [])[0]"
                :options="[{value: 'tall', label: 'Tall'}, {value: 'wide', label: 'Wide'}]"
            />

            <TextFormGroup
                v-model="data.video_url"
                name="video_url"
                label="Top Video URL"
                help-text="The URL of a video to include in the article."
                :error="(errors.video_url || [])[0]"
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Body
          </h3>

          <p>
            These panels contain the main text of the article posting. You can add more blocks to the main text by
            using the
            button below.
          </p>

          <div class="btn-group" style="margin-top: 4px">
            <button type="button" class="btn btn-black btn-sm dropdown-toggle" data-toggle="dropdown">
              <i class="fa fa-plus"></i> Add <span class="caret"></span>
            </button>

            <ul class="dropdown-menu">
              <li v-for="bodyCategoryOption in bodyCategoryOptions">
                <a href="#"
                   @click.prevent="data.content_bodies.push({body_category: bodyCategoryOption.value, body_category_parameters: {}})"
                >
                  {{ bodyCategoryOption.label }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <Draggable
          element="div"
          v-model="data.content_bodies"
          handle=".handle"
          chosen-class="interval--dragging"
          class="col-xs-18"
      >
        <ContentBodyFormPanel
            v-for="(contentBody, index) in data.content_bodies"
            v-model="data.content_bodies[index]"
            :key="index"
            :errors="getBodyCategoryParametersErrors(index)"
            :initial-collapsed="edit"
            :company-id="companyId"
            @remove="data.content_bodies.splice(index, 1)"
        />
      </Draggable>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Article Details
          </h3>

          <p>
            In this panel, you can enter the specific details about the article.
          </p>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <SelectFormGroup
                v-model="data.editorial_type"
                name="editorial_type"
                label="Editorial Type"
                help-text="The editorial type of the article."
                :error="(errors.editorial_type || [])[0]"
                :options="[{value: 'Spor 1', label: 'Spor 1'}, {value: 'Spor 2', label: 'Spor 2'}]"
                :nullable="true"
            />

            <SelectFormGroup
                v-model="data.content_article_category_id"
                name="content_article_category_id"
                label="Content Article Category"
                :error="(errors.content_article_category_id || [])[0]"
                :options="getContentArticleCategoryOptions(portal)"
            />

            <SelectFormGroup
                v-model="data.content_article_label_id"
                name="content_article_label_id"
                label="Content Article Label"
                :error="(errors.content_article_label_id || [])[0]"
                :options="contentArticleLabelOptions"
                :nullable="true"
            />

            <ContentArticleTagsFormGroup
                v-model="tags"
                name="tags"
                label="Content Article Tags"
                :error="(errors.content_article_tags || [])[0]"
                :portal="portal"
            />
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Mentioned Companies
          </h3>

          <p>
            Set the companies mentioned in the article.
          </p>

          <button type="button" class="btn btn-black btn-sm" @click="data.content_article_references.push({})">
            <i class="fa fa-plus"></i> Add
          </button>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <table class="table table-condensed table-striped">
              <thead>
              <tr>
                <th class="col-xs-9">
                  Company Name
                </th>

                <th class="col-xs-9">
                  Portal Alias
                </th>

                <th class="col-xs-6"></th>
              </tr>
              </thead>

              <tbody>
              <tr v-for="(row, index) in data.content_article_references">
                <td v-if="data.content_article_references[index].portal_company_id">
                  <div
                      :class="{'has-error': (errors['content_article_references.' + index + '.portal_company_id'] || []).length}"
                  >
                    {{ data.content_article_references[index].company_name }}

                    <span
                        class="help-block"
                        v-if="(errors['content_article_references.' + index + '.portal_company_id'] || []).length"
                    >
                      {{ (errors['content_article_references.' + index + '.portal_company_id'] || [])[0] }}
                    </span>
                  </div>
                </td>

                <td v-if="data.content_article_references[index].portal_company_id">
                  {{ data.content_article_references[index].portal_company_name }}
                </td>

                <td v-else colspan="2">
                  <div
                      class="text-center"
                      :class="{'has-error': (errors['content_article_references.' + index + '.portal_company_id'] || []).length}"
                  >
                    (none selected)

                    <span
                        class="help-block"
                        v-if="(errors['content_article_references.' + index + '.portal_company_id'] || []).length"
                    >
                      {{ (errors['content_article_references.' + index + '.portal_company_id'] || [])[0] }}
                    </span>
                  </div>
                </td>

                <td class="text-right">
                  <button
                      type="button"
                      class="btn btn-xs btn-black"
                      @click="selectContentArticleReference(index)"
                  >
                    <i class="fa fa-mouse-pointer"/> Change
                  </button>

                  <button
                      type="button"
                      class="btn btn-xs btn-black"
                      @click="data.content_article_references.splice(index, 1)"
                  >
                    <i class="fa fa-minus"></i> Remove
                  </button>
                </td>
              </tr>

              <tr v-if="!data.content_article_references.length">
                <td colspan="3" class="text-center">
                  The article does not mention any companies.
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Promoted Scheduling
          </h3>

          <p>
            Schedule flags for the promoted article.
          </p>

          <button type="button" class="btn btn-black btn-sm" @click="data.content_article_flag_schedules.push({})">
            <i class="fa fa-plus"></i> Add
          </button>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <div v-if="data.content_article_flag_schedules.length !== 0">
              <div class="row">
                <div class="col-xs-7">
                  <label>
                    From
                  </label>
                </div>

                <div class="col-xs-7">
                  <label>
                    To
                  </label>
                </div>

                <div class="col-xs-6">
                  <label>
                    Flag
                  </label>
                </div>
              </div>

              <div class="row" v-for="(row, index) in data.content_article_flag_schedules" style="margin-bottom: 15px">
                <div class="col-xs-7"
                     :class="{'has-error': (errors['content_article_flag_schedules.' + index + '.starts_at'] || []).length}">
                  <DateWidget
                      v-model="data.content_article_flag_schedules[index].starts_at"
                      format="YYYY-MM-DD HH:mm:ss"
                      original-format="YYYY-MM-DD HH:mm:ss"
                  />

                  <span class="help-block"
                        v-if="(errors['content_article_flag_schedules.' + index + '.starts_at'] || []).length">
                  {{ (errors['content_article_flag_schedules.' + index + '.starts_at'] || [])[0] }}
                </span>
                </div>

                <div class="col-xs-7"
                     :class="{'has-error': (errors['content_article_flag_schedules.' + index + '.ends_at'] || []).length}">
                  <DateWidget
                      v-model="data.content_article_flag_schedules[index].ends_at"
                      format="YYYY-MM-DD HH:mm:ss"
                      original-format="YYYY-MM-DD HH:mm:ss"
                  />

                  <span class="help-block"
                        v-if="(errors['content_article_flag_schedules.' + index + '.ends_at'] || []).length">
                  {{ (errors['content_article_flag_schedules.' + index + '.ends_at'] || [])[0] }}
                </span>
                </div>

                <div class="col-xs-6"
                     :class="{'has-error': (errors['content_article_flag_schedules.' + index + '.content_article_flag_id'] || []).length}">
                  <SelectWidget
                      v-model="data.content_article_flag_schedules[index].content_article_flag_id"
                      :options="contentArticleFlagOptions"
                  />

                  <span class="help-block"
                        v-if="(errors['content_article_flag_schedules.' + index + '.content_article_flag_id'] || []).length">
                  {{ (errors['content_article_flag_schedules.' + index + '.content_article_flag_id'] || [])[0] }}
                </span>
                </div>

                <div class="col-xs-4">
                  <button type="button" class="btn btn-default"
                          @click="data.content_article_flag_schedules.splice(index, 1)">
                    <i class="fa fa-minus"></i> Remove
                  </button>
                </div>
              </div>
            </div>

            <div v-else>
              The article has not been scheduled.
            </div>
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-6">
        <div class="help-text">
          <h3>
            Publication
          </h3>

          <p>
            In this panel, you can adjust the publication settings for the article.
          </p>

          <button v-if="edit" type="button" class="btn btn-black btn-sm" @click="$emit('copy')">
            <i class="fa fa-share"></i> Share
          </button>

          <button v-if="edit" type="button" class="btn btn-black btn-sm" @click="$emit('mail')">
            <i class="fa fa-envelope"></i> Mail
          </button>
        </div>
      </div>

      <div class="col-xs-18">
        <div class="panel panel-default">
          <div class="panel-body">
            <DateFormGroup
                v-model="data.publish_at"
                name="publish_at"
                label="Publish At"
                help-text="The date that the article should be published."
                :error="(errors.publish_at || [])[0]"
                format="YYYY-MM-DD HH:mm:ss"
                original-format="YYYY-MM-DD HH:mm:ss"
            />

            <DateFormGroup
                v-model="data.unpublish_at"
                name="unpublish_at"
                label="Unpublish At"
                help-text="The date that the article should be unpublished."
                :error="(errors.unpublish_at || [])[0]"
                format="YYYY-MM-DD HH:mm:ss"
                original-format="YYYY-MM-DD HH:mm:ss"
            />

            <StaticFormGroup
                v-if="edit"
                label="Author"
                :value="author"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="!edit">
      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <div class="help-text">
            <h3>
              Share
            </h3>

            <p>
              Select which portals to share the new article with.
            </p>
          </div>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <table class="table table-striped">
                <thead>
                <tr>
                  <th>
                    Portal
                  </th>

                  <th>
                    Article Category
                  </th>
                </tr>
                </thead>

                <tbody>
                <tr v-for="portal in filteredPortals">
                  <td>
                    {{ portal.attributes.name }}
                  </td>

                  <td>
                    <SelectWidget
                        v-model="data.copies[portal.id]"
                        class="input-sm"
                        placeholder="(don't share)"
                        :options="getContentArticleCategoryOptions(portal)"
                        :nullable="true"
                    />
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="edit">
      <hr class="panel-separator">

      <div class="row">
        <div class="col-xs-6">
          <div class="help-text">
            <h3>
              Advanced Setup
            </h3>

            <p>
              These settings control the advanced setup for the article.
            </p>
          </div>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <StaticFormGroup
                  :value="data.legacy_reference"
                  label="Legacy Reference"
                  help-text="If the article was published on a legacy platform, this is the reference to the old article."
              />

              <StaticFormGroup
                  :value="data.path"
                  label="Path"
                  help-text="The path of the article."
              />

              <TextFormGroup
                  v-model="data.canonical_link"
                  name="canonical_link"
                  label="Canonical Link"
                  help-text="The canonical link of the article."
                  :error="(errors.canonical_link || [])[0]"
              />

              <RadioFormGroup
                  v-model="data.has_render_log"
                  name="has_render_log"
                  label="Enable render log"
                  :error="(errors.has_render_log || [])[0]"
                  :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                  help-text="By enabling the render log, each individual rendition of the article will be logged for inspection."
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <hr class="panel-separator">

    <div class="row">
      <div class="col-xs-offset-6 col-xs-18">

        <button type="submit" class="btn btn-black pull-left" style="margin-bottom: 22px">
          <i class="fa fa-save"></i> Save
        </button>

        <a
            v-if="url"
            :href="url + '?preview=true'"
            class="btn btn-white pull-right"
            target="_blank"
            style="border: 1px solid #000000;"
        >
          <i class="fas fa-external-link-alt"></i> Preview
        </a>

        <div class="clearfix"></div>
      </div>
    </div>

    <SelectPortalCompanyModal :portal="portal" @selected="contentArticleReferenceSelected"/>
  </form>
</template>

<script>
import store from '@/store'
import BodyCategoryParameterForm from "../../ContentBodies/BodyCategoryParameterForms/Base.vue";
import ContentBodyFormPanel from "../../ContentBodies/Panels/Form.vue";
import DateFormGroup from '@/vendor/forms/FormGroups/Block/Date'
import Draggable from 'vuedraggable'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Block/Submit'
import TextareaFormGroup from '@/vendor/forms/FormGroups/Block/Textarea'
import TextFormGroup from '@/vendor/forms/FormGroups/Block/Text'
import DateWidget from '@/vendor/forms/FormGroups/Widgets/Date'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
import SelectPortalCompanyModal from '@/components/Administration/PortalCompanies/Modals/Select'
import SelectFormGroup from "../../../../vendor/forms/FormGroups/Block/Select.vue";
import RadioFormGroup from "../../../../vendor/forms/FormGroups/Block/Radio.vue";
import StaticFormGroup from "../../../../vendor/forms/FormGroups/Block/Static.vue";
import ContentArticleTagsFormGroup from '../../../FormGroups/Block/ContentArticleTags'
import GalleryFormGroup from "../../../FormGroups/Block/Gallery"

export default {
  components: {
    ContentArticleTagsFormGroup,
    DateWidget,
    Draggable,
    SelectFormGroup,
    RadioFormGroup,
    StaticFormGroup,
    DateFormGroup,
    SubmitFormGroup,
    TextareaFormGroup,
    TextFormGroup,
    SelectWidget,
    BodyCategoryParameterForm,
    ContentBodyFormPanel,
    GalleryFormGroup,
    SelectPortalCompanyModal
  },

  props: {
    initial: {
      required: false,
      type: Object,
      default: () => ({})
    },

    errors: {
      required: true,
      type: Object
    },

    edit: {
      required: false,
      default: false
    },

    url: {
      required: false,
      type: String
    },

    contentArticleCategories: {
      required: true,
      type: Array
    },

    contentArticleLabels: {
      required: true,
      type: Array
    },

    contentArticleFlags: {
      required: true,
      type: Array
    },

    portals: {
      required: true,
      type: Array
    },

    portal: {
      required: true,
      type: Object
    },

    companyId: {
      required: true,
      type: [String, Number]
    }
  },

  data() {
    return {
      data: {...this.initial},

      tags: this.initial.content_article_tags.map(contentArticleTag => ({text: contentArticleTag.name})),

      shared: store,

      editingContentArticleReference: null,
    }
  },

  methods: {
    submit() {
      this.$emit('submit', {...this.data, content_article_tags: this.tags.map(tag => ({name: tag.text}))})
    },

    getBodyCategoryParametersErrors(index) {
      return Object.entries(this.errors)
          .filter(entry => entry[0].startsWith('content_bodies.' + index + '.body_category_parameters.'))
          .reduce((result, entry) => ({
            ...result,
            [entry[0].substring(('content_bodies.' + index + '.body_category_parameters.').length)]: entry[1]
          }), {})
    },

    selectContentArticleReference(index) {
      this.editingContentArticleReference = index

      $('#select-portal-company-modal').modal('show')
    },

    contentArticleReferenceSelected(value) {
      this.data.content_article_references.splice(this.editingContentArticleReference, 1, value)
    },

    getContentArticleCategoryOptions(portal) {
      return this.contentArticleCategories
          .filter(contentArticleCategory => contentArticleCategory.attributes.portal_id == portal.id)
          .map(contentArticleCategory => ({
            value: contentArticleCategory.id,
            label: contentArticleCategory.attributes.name,
            disabled: !contentArticleCategory.attributes.is_active
          }))
    },

  },

  computed: {
    bodyCategoryOptions() {
      return [
        {value: 'text', label: 'Text'},
        {value: 'fact-box', label: 'Fact Box'},
        {value: 'quote', label: 'Quote'},
        {value: 'image', label: 'Image'},
        {value: 'gallery', label: 'Gallery'},
        {value: 'podcast', label: 'Podcast'},
        {value: 'social-media', label: 'Social Media'},
        {value: 'video', label: 'Video'},
      ]
    },

    contentArticleLabelOptions() {
      return this.contentArticleLabels.map(contentArticleLabel => ({
        value: contentArticleLabel.id,
        label: contentArticleLabel.attributes.name
      }))
    },

    contentArticleFlagOptions() {
      return this.contentArticleFlags.map(contentArticleFlag => ({
        value: contentArticleFlag.id,
        label: contentArticleFlag.attributes.name
      }))
    },

    author() {
      return [
        this.data.author_name,
        this.data.author_email,
      ].join('&nbsp;&middot;&nbsp;')
    },

    filteredPortals() {
      return this.portals.filter(portal => portal.id !== this.portal.id)
    }
  },
}
</script>